import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import { WorkOrder, WorkOrderSparePartCustody } from '../../models/work-order.model';
import { WorkOrderActions } from './work-orders.actions';
import { CylindersActions } from '../cylinders/cylinders.actions';

export interface WorkOrdersState extends EntityState<WorkOrder> {
  loading: boolean;
  error: string | null;
  // Part B/Phase 8 — keyed by workOrderId, mirrors CylindersState's `movements` slice shape.
  sparePartCustody: Record<number, WorkOrderSparePartCustody[]>;
}

export const adapter = createEntityAdapter<WorkOrder>();

const initialState: WorkOrdersState = adapter.getInitialState({
  loading: false,
  error: null,
  sparePartCustody: {},
});

export const workOrdersFeature = createFeature({
  name: 'workOrders',
  reducer: createReducer(
    initialState,
    on(WorkOrderActions.loadWorkOrders, (state) => ({ ...state, loading: true, error: null })),
    on(WorkOrderActions.loadWorkOrdersSuccess, (state, { workOrders }) =>
      adapter.setAll(workOrders, { ...state, loading: false })),
    on(WorkOrderActions.loadWorkOrdersFailure, (state, { error }) => ({ ...state, loading: false, error })),

    on(WorkOrderActions.loadWorkOrderSuccess, (state, { workOrder }) => adapter.upsertOne(workOrder, state)),
    on(WorkOrderActions.createWorkOrderSuccess, (state, { workOrder }) => adapter.addOne(workOrder, state)),
    on(WorkOrderActions.attachCylinderSuccess, (state, { workOrder }) => adapter.upsertOne(workOrder, state)),
    on(WorkOrderActions.issueWorkOrderSuccess, (state, { workOrder }) => adapter.upsertOne(workOrder, state)),
    on(WorkOrderActions.completeWorkOrderSuccess, (state, { workOrder }) => adapter.upsertOne(workOrder, state)),
    on(WorkOrderActions.workOrderFailure, (state, { error }) => ({ ...state, error })),

    // Part B/Phase 8 — clearing cylinder custody always detaches (Phase 7's ClearCustodyAsync
    // releases as its last step), so the work order that held it loses that cylinder id. Mirrors
    // the old detachCylinderSuccess local-patch this replaces (the bare, unbilled detach action
    // is gone — every detach now goes through clear-custody).
    on(CylindersActions.clearCylinderCustodySuccess, (state, { workOrderId, cylinder }) => {
      const existing = state.entities[workOrderId];
      if (!existing) return state;
      return adapter.updateOne({
        id: workOrderId,
        changes: { attachedCylinderIds: existing.attachedCylinderIds.filter(id => id !== cylinder.id) }
      }, state);
    }),

    on(WorkOrderActions.loadSparePartCustodySuccess, (state, { workOrderId, custody }) => ({
      ...state, sparePartCustody: { ...state.sparePartCustody, [workOrderId]: custody }
    })),
  )
});

export const {
  name: workOrdersFeatureKey,
  reducer,
  selectWorkOrdersState,
  selectLoading: selectWorkOrdersLoading,
  selectError: selectWorkOrdersError,
} = workOrdersFeature;

export const { selectAll, selectEntities } = adapter.getSelectors();
